// Modal.tsx
import React, { useEffect, useCallback } from 'react';
import { CloseIcon } from './Icons';
import styles from './Modal.module.css';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEsc?: boolean;
}

const Modal: React.FC<ModalProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  children,
  footer,
  size = 'md',
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEsc = true
}) => {

  const handleEscKey = useCallback((event: KeyboardEvent) => {
    if (closeOnEsc && event.key === 'Escape') {
      onClose();
    }
  }, [closeOnEsc, onClose]);

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnBackdropClick && event.target === event.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleEscKey]);

  return (
    <div
      className={`${styles.modal} ${isOpen ? styles.modalOpen : styles.modalClosed}`}
      onClick={handleBackdropClick}
      aria-modal="true"
      role="dialog"
      aria-label={title}
    >
      <div
        className={`${styles.content} ${styles[`content${size.charAt(0).toUpperCase() + size.slice(1)}`]} ${isOpen ? styles.contentOpen : styles.contentClosed}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          {title && (
            <div>
              <h2 className={styles.title}>{title}</h2>
            </div>
          )}

          {showCloseButton && (
            <button
              onClick={onClose}
              className={styles.close}
              aria-label="Close modal"
            >
              <CloseIcon size={20} />
            </button>
          )}
        </div>

        <div className={styles.body}>
          {children}
        </div>

        {footer ? (
          <div className={styles.footer}>
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Modal;